import { Component, Input, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';
import { InstrumentDetailFullOut } from 'src/app/services/rest-api/rest-api';
import { PUBLIC_ROUTES, SortDirections } from '../constants';
import { sortInstruments } from '../functions';
import { AbstractControl, FormControl, UntypedFormArray, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { Subscription } from 'rxjs';
import { UserService } from 'src/app/services/user/user.service';
import { ConditionControl, InstrumentCategories } from '../charts/charts';
import { StrategyInstrumentsFormService } from 'src/app/services/strategy-instruments-form.service';
import { Router } from '@angular/router';

export type AvailablePerformanceYears = 1 | 3 | 5;
const NO_RATING_TRANSLATE_KEY = 'strategies.filtrationValues.noRating';

interface FilterInput {
  value: boolean;
  name: string | number,
}

@Component({
  selector: 'libero-listable-instruments',
  templateUrl: './listable-instruments.component.html',
  styleUrl: './listable-instruments.component.scss',
  standalone: false
})
export class ListableInstrumentsComponent implements OnChanges, OnDestroy {
  @Input() public availableInstruments?: InstrumentDetailFullOut[];
  @Input() public refreshPagination?: boolean;
  @Input() public hideAddButtons = false;
  @Input() public editId?: number | null;

  public filtrationForm!: UntypedFormGroup;
  private formSubscription?: Subscription;

  public sortDirections = SortDirections;
  public sortDirection: SortDirections | null = null;

  public selectedPerformance: AvailablePerformanceYears = 1;

  public filtrationVisible = false;

  public filteredInstruments: InstrumentDetailFullOut[] = [];

  public constructor(
    private fb: UntypedFormBuilder,
    private userService: UserService,
    public instrumentsService: StrategyInstrumentsFormService,
    private router: Router,
  ) {}

  public toggleFiltration(): void {
    this.filtrationVisible = !this.filtrationVisible;
  }

  public ngOnChanges(changes: SimpleChanges):void {
    if (changes['availableInstruments']) {
      this.initFilters();
      this.filterData();
      this.initFormvalueSubscribe();
    }
  }

  public applySort(direction: SortDirections): void {
    this.sortDirection =
      this.sortDirection === direction ? null : direction;
    this.sortFilteredInstruments();
  }

  private sortFilteredInstruments(): void {
    this.filteredInstruments = JSON.parse(JSON.stringify(this.filteredInstruments?.sort(sortInstruments(this.sortDirection)))) ?? [];
    this.refreshPagination = !this.refreshPagination;
  }

  public changePerformance(newPerformance: AvailablePerformanceYears): void {
    this.selectedPerformance = newPerformance;
  }

  public setActiveFilters(): void {
    function mapToTrue(val: FilterInput): FilterInput {
      return {
        name: val.name,
        value: true,
      }
    }

    const updatedFilters: Record<string, FilterInput> = {};

    for (const filterKey in this.filtrationForm.value) {
      if(typeof this.filtrationForm.value[filterKey] !== 'string' && !(this.filtrationForm.value[filterKey] instanceof String)) {
        updatedFilters[filterKey] =  this.filtrationForm.value[filterKey].map(mapToTrue);
      }
    }

    this.filtrationForm.patchValue({ searchString: '' });
    this.filtrationForm.patchValue(updatedFilters);

    this.filterData();
  }

  private initFilters() {
    const availableClasses = new Set(this.availableInstruments?.map(instrument => instrument.instrumentCategory));
    const availableTargets = new Set(this.availableInstruments?.map(instrument => instrument.instrumentTarget));
    const availableRegions = new Set(this.availableInstruments?.flatMap(instrument => instrument.regionalOrientations));
    const availableCurrencies = new Set(this.availableInstruments?.map(instrument => instrument.currencyCode));
    const availableRiskScores = new Set(this.availableInstruments?.map(instrument => instrument.riskScore).sort());
    const availableIncomeTypes = new Set(this.availableInstruments?.map(instrument => instrument.instrumentIncomeType));
    const availableEsgs = new Set(this.availableInstruments?.map(instrument => instrument.esg));

    this.filtrationForm = new UntypedFormGroup({
      instrumentCategory: this.generateFormArray(availableClasses),
      instrumentTarget: this.generateFormArray(availableTargets),
      regionalOrientations: this.generateFormArray(availableRegions),
      currencyCode: this.generateFormArray(availableCurrencies),
      riskScore: this.generateFormArray(availableRiskScores),
      instrumentIncomeType: this.generateFormArray(availableIncomeTypes),
      esg: this.generateFormArray(availableEsgs, true),
      searchString: new FormControl<string>(''),
    });
  }

    private filterData(): void {
    this.filteredInstruments = [];
    let instruments: InstrumentDetailFullOut[] = JSON.parse(JSON.stringify(this.availableInstruments));

    for (const formControlName of Object.keys(this.filtrationForm.controls)) {
      if (this.filtrationForm.controls[formControlName] instanceof UntypedFormControl) {
        instruments = this.filterBySearchString(this.filtrationForm.controls[formControlName], instruments);
      } else if (this.filtrationForm.controls[formControlName] instanceof UntypedFormArray) {
        instruments = this.filterByCheckboxArray(formControlName, instruments);
      } else {
        // some API data still comming with null values
        console.error('Unexpected form input type: ', formControlName);
      }
    }

    if(!this.router.url.includes(PUBLIC_ROUTES.PUBLIC_INSTRUMENTS)) {
      const allowedCategories = this.userService.loggedUser?.instrumentCategories ?? [];
      // filter by client available preferences
      this.filteredInstruments = instruments.filter(
        (i: InstrumentDetailFullOut) =>
          // Either instrument must be of questionnaire's allowed instrument category (new strategy)
          (allowedCategories.some(
            (ic) => ic === i.instrumentCategory
            // And it mas not be marked for removal
          ) && !i.willBeRemoved && i.insertable)
          // Or it must be in the current strategy's existing instruments (edited strategy)
          || (this.editId &&
          this.instrumentsService.strategyOriginalInstruments?.controls.some(soi => soi.value.id === i.instrumentId))
      ).sort((a, b) => a.instrumentId - b.instrumentId);
    } else {
      // public - show all
      this.filteredInstruments = instruments;
    }
    this.sortFilteredInstruments();
  }

    private filterByCheckboxArray(filterControlName: string, instruments: InstrumentDetailFullOut[]): InstrumentDetailFullOut[] {
    const conditions = this.filtrationForm.controls[filterControlName].value as ConditionControl[];
    const checkedConditions =
      conditions.filter(condition => condition.value).map(condition => condition.name);

    if (filterControlName === 'regionalOrientations') {
      return instruments.filter(
        instrument => instrument.regionalOrientations.some(condition => checkedConditions.includes(condition as InstrumentCategories)
      ));
    } else {
      if(filterControlName === 'esg' && NO_RATING_TRANSLATE_KEY) {
        this.replaceTranslateKey(checkedConditions);
      }
      return instruments.filter(instrument => checkedConditions.includes(instrument[filterControlName]));
    }
  }

    private replaceTranslateKey(checkedConditions: (string | null)[]) {
    const index = checkedConditions.indexOf(NO_RATING_TRANSLATE_KEY);

    if (index !== -1) {
      checkedConditions[index] = null;
    }
  }

    private generateFormArray(controlsData: Set<string | number>, esg = false): UntypedFormArray {
    const formArray = new UntypedFormArray([]);

    for (const checkbox of [...controlsData]) {
      if(checkbox !== null) {
        formArray.push(this.createCheckboxControl(checkbox));
      } else if(esg) {
        const esgControl = this.fb.group({
          name: NO_RATING_TRANSLATE_KEY,
          value: true,
          translate: true
        });

        formArray.push(esgControl);
      }
    }

    return formArray;
  }

  private createCheckboxControl(checkboxName: string | number): AbstractControl {
    return this.fb.group({
      name: checkboxName,
      value: true, // true default
    })
  }

  private filterBySearchString(filterControl: AbstractControl, instruments: InstrumentDetailFullOut[]): InstrumentDetailFullOut[] {
    const parsedSearchValue = filterControl.value.toLowerCase().trim();
    return instruments.filter(instrument => (
      instrument.humanName.toLowerCase().includes(parsedSearchValue) ||
      instrument.name.toLowerCase().includes(parsedSearchValue) ||
      instrument.isin.toLowerCase().includes(parsedSearchValue)
    ));
  }

  private initFormvalueSubscribe() {
    this.formSubscription = this.filtrationForm.valueChanges.pipe().subscribe( () => this.filterData() );
  }

  public ngOnDestroy(): void {
    this.formSubscription?.unsubscribe();
  }
}
